import type {ReactNode} from 'react';
import type {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import type {BuyCreditsResponse} from '@/types/credits.types';
import type {DownloadCheckoutAJAXResponseOrderSuccess} from '@Libraries/poster-download-library';

/** Legacy modal root id; kept because the Playwright checkout page object anchors on it. */
export const LEGACY_CHECKOUT_MODAL_ROOT_ID = 'stripe-checkout-container';

export const CHECKOUT_WEBHOOK_POLLING_TIMEOUT_MS = 120_000;
export const CHECKOUT_REDIRECT_LINK_DISABLED_SECONDS = 10;
export const CHECKOUT_PROMO_INPUT_REVEAL_DELAY_MS = 2_000;
export const SUBSCRIPTION_PROMOTION_CODE_COOKIE = 'pmwspc';
export const DEFAULT_TRIAL_SOURCE = 'trial_dialog';
export const NO_PROMO_CODE_SENTINEL = '-';
export const BUY_CREDITS_REQUEST_TRIAL_CONVERSION_SOURCE = 'buy-credits';
export const POST_CHECKOUT_ADD_TEAM_MEMBERS_SOURCE = 'post-checkout';
export const SUPPORT_REQUEST_URL = 'https://support.postermywall.com/hc/en-us/requests/new';

export enum CheckoutMode {
  SUBSCRIPTION = 'subscription',
  CREDIT_PACK = 'creditPack',
  DOWNLOAD = 'download',
  PREMIUM_CONFIRMATION = 'premiumConfirmation',
  WELCOME_TO_TEAM = 'welcomeToTeam',
  PAYMENT_PROCESSING = 'paymentProcessing',
}

/** `mode` values understood by `checkout/getCheckoutDialogData` (mirrors `StripeCheckoutParams::MODE_*`). */
export enum CheckoutDialogDataMode {
  SUBSCRIPTION = 'subscription',
  PAYMENT = 'payment',
  PREMIUM_CONFIRMATION = 'premiumconfirmation',
  WELCOME_TO_TEAM = 'welcometoteam',
}

/** Stripe Checkout Session modes returned by `checkout/getCheckoutModeForSession`. */
export enum StripeSessionMode {
  SUBSCRIPTION = 'subscription',
  PAYMENT = 'payment',
  SETUP = 'setup',
}

export enum CheckoutStep {
  PREFLIGHT = 'preflight',
  EMAIL_REQUIRED = 'emailRequired',
  CREDIT_PACKS = 'creditPacks',
  PAYMENT_OPTIONS = 'paymentOptions',
  BILLING_ADDRESS_LIST = 'billingAddressList',
  BILLING_ADDRESS_FORM = 'billingAddressForm',
  STRIPE_EMBEDDED = 'stripeEmbedded',
  FINALIZING = 'finalizing',
  PREMIUM_CONFIRMATION = 'premiumConfirmation',
  ERROR = 'error',
}

/** Below this viewport width the credit-pack step drops its side-by-side layout and the flow shows a panel header. */
export const CHECKOUT_COMPACT_LAYOUT_MAX_WIDTH = 900;
/** From this width up the hand-off draws a centred dialog, so only phones get the bottom sheet — a tablet does not. */
export const CHECKOUT_DIALOG_MIN_VIEWPORT_WIDTH = 768;
export const CREDIT_PACKS_TWO_COLUMN_MODAL_WIDTH = '1000px';
export const CREDIT_PACKS_TWO_COLUMN_MODAL_HEIGHT = '629px';
/** The pack the design highlights as "Popular" and pre-selects. */
export const POPULAR_CREDIT_PACK_QUANTITY = 1000;

/** Widths mirror the legacy dialog: 650px by default, 550px for the address form, 1000px for the confirmation carousel. The Stripe form steps start at the compact width and widen through `getModalWidthForStep` when two columns fit. */
export const CHECKOUT_MODAL_WIDTH_BY_STEP: Record<CheckoutStep, string> = {
  [CheckoutStep.PREFLIGHT]: '650px',
  [CheckoutStep.EMAIL_REQUIRED]: '650px',
  [CheckoutStep.CREDIT_PACKS]: '650px',
  [CheckoutStep.PAYMENT_OPTIONS]: '650px',
  [CheckoutStep.BILLING_ADDRESS_LIST]: '650px',
  [CheckoutStep.BILLING_ADDRESS_FORM]: '550px',
  [CheckoutStep.STRIPE_EMBEDDED]: '650px',
  [CheckoutStep.FINALIZING]: '650px',
  [CheckoutStep.PREMIUM_CONFIRMATION]: '1000px',
  [CheckoutStep.ERROR]: '650px',
};

/** Legacy's expanded width for the embedded form; the modal's own max-width trims it to the viewport. */
export const STRIPE_TWO_COLUMN_MODAL_WIDTH = '1200px';
/**
 * Stripe's embedded form lays out two columns from 992px of iframe width and collapses to a single column below it,
 * so the modal only goes wide when it can hand Stripe those 992px: 992 + the modal's 40px side margins + a scrollbar
 * allowance. Any narrower and the modal stays compact instead of framing a single column in empty space.
 */
export const STRIPE_TWO_COLUMN_MIN_VIEWPORT_WIDTH = 1096;

export interface SubscriptionTrackingData {
  idSubscriptionPurchase: number;
  orderValue: number;
  isResubscription: number;
  planType: string;
  term: string;
  seats: number;
  upsellDialogName: string;
  ga4EventSource: string;
  sutm: string;
}

export type GA4PurchaseEventParams = Record<string, unknown>;

export interface SubscriptionCheckoutResult {
  trialFailError?: string;
  idSubscriptionPurchase?: number;
  idSubscriptionPlan?: number;
  orderValue?: number;
  ga4PurchaseEventParams?: GA4PurchaseEventParams | null;
  trackingData?: SubscriptionTrackingData;
}

export interface CreditPackPurchaseResult extends BuyCreditsResponse {
  ga4PurchaseEventParams?: GA4PurchaseEventParams;
  invoiceHTML?: string;
  invoiceMobileHTML?: string;
}

export interface DownloadPurchaseResult extends DownloadCheckoutAJAXResponseOrderSuccess {
  orderType?: string;
  orderValue?: number;
  ga4PurchaseEventParams?: GA4PurchaseEventParams;
}

export type CheckoutSuccessResult = SubscriptionCheckoutResult | CreditPackPurchaseResult | DownloadPurchaseResult;

interface CheckoutRequestCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
}

export interface SubscriptionCheckoutRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.SUBSCRIPTION;
  priceId: number;
  seats: number;
  price: number;
  promoCode: string;
  isTrial: boolean;
  trialSource: string;
  isResubscribe: boolean;
  posterHashedId: string;
}

export interface CreditPackCheckoutRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.CREDIT_PACK;
  headingText: string;
  modalTitle: string;
  onSuccess?: (data: CreditPackPurchaseResult) => void;
}

export interface DownloadCheckoutRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.DOWNLOAD;
  priceId: number;
  cartId: number;
  posterHashedId: string;
  productId: number;
  isSilent: boolean;
  onSuccess?: (data: DownloadPurchaseResult) => void;
}

export interface PremiumConfirmationRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.PREMIUM_CONFIRMATION;
  ga4PurchaseEventParams?: GA4PurchaseEventParams | null;
  trackingData?: SubscriptionTrackingData;
}

export interface WelcomeToTeamRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.WELCOME_TO_TEAM;
}

export interface PaymentProcessingRequest extends CheckoutRequestCallbacks {
  mode: CheckoutMode.PAYMENT_PROCESSING;
  sessionId: string;
}

export type CheckoutRequest =
  | SubscriptionCheckoutRequest
  | CreditPackCheckoutRequest
  | DownloadCheckoutRequest
  | PremiumConfirmationRequest
  | WelcomeToTeamRequest
  | PaymentProcessingRequest;

/** What a caller supplies: the mode is the opener's business, not the call site's. */
export type SubscriptionCheckoutOptions = Omit<SubscriptionCheckoutRequest, 'mode'>;
export type CreditPackCheckoutOptions = Omit<CreditPackCheckoutRequest, 'mode'>;
export type DownloadCheckoutOptions = Omit<DownloadCheckoutRequest, 'mode'>;

export interface CheckoutCreditPack {
  id: number;
  priceId: number | null;
  name: string;
  quantity: number;
  amount: number;
}

export interface CheckoutBillingAddress {
  id: number;
  streetAddress1: string;
  streetAddress2: string;
  contactName: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  phone: string;
}

export interface CheckoutDialogData {
  premiumLevel: number;
  isTrial: boolean;
  mode: CheckoutDialogDataMode;
  creditPacks: CheckoutCreditPack[];
  currencyCode: string;
  paypalEnvironment: string;
  addresses: CheckoutBillingAddress[];
  isEmailRequired: boolean;
  isCurrencyBlockedForPaypal: boolean;
  isUserInNL: boolean;
  isUserInIndia: boolean;
  usesRedirectReturnFlow: boolean;
  teamName: string;
  addressFormOptions: CheckoutAddressFormOptions;
}

export interface CheckoutAddressFormOptions {
  countries: Record<string, string>;
  usStates: Record<string, string>;
}

export interface IosCreditPackPrice {
  priceId: string;
  displayPrice: string;
  currencyCode: string;
}

export interface CheckoutPromoState {
  code: string;
  isApplied: boolean;
  appliedText: string;
  errorMessage: string;
}

export interface CheckoutStripeSession {
  sessionId: string;
  clientSecret: string;
}

export interface CheckoutPollingIds {
  invoiceId: string;
  chargeId: string;
  paymentIntentId: string;
  setupIntentId: string;
}

export interface CheckoutConfirmationData {
  ga4PurchaseEventParams: GA4PurchaseEventParams | null;
  trackingData: SubscriptionTrackingData | null;
}

export interface CheckoutFlowState {
  isActive: boolean;
  mode: CheckoutMode | null;
  step: CheckoutStep;
  transitionDirection: TransitionDirection;
  isLoading: boolean;
  dialogData: CheckoutDialogData | null;
  selectedCreditPackPriceId: number | null;
  iosCreditPackPrices: Record<number, IosCreditPackPrice>;
  promo: CheckoutPromoState;
  addresses: CheckoutBillingAddress[];
  selectedBillingAddressId: number | null;
  stripeSession: CheckoutStripeSession | null;
  pollingIds: CheckoutPollingIds;
  confirmation: CheckoutConfirmationData | null;
  isCreditBenefitsViewOpen: boolean;
  errorTitle: string;
  errorMessage: string;
}

export interface GoToStepPayload {
  step: CheckoutStep;
  direction: TransitionDirection;
}

export interface StripeClientSecretResponse {
  sessionId: string;
  clientSecret: string;
}

export interface WebhookPollingRequest {
  uri: string;
  data: Record<string, string>;
}

export interface CheckoutSessionModeResponse {
  mode: StripeSessionMode;
}

export interface CanBuyCreditsResponse {
  canBuyCredits: boolean;
}

export interface UserCurrentSubscriptionStatus {
  vendor: string;
  isInactive: boolean;
  awaitingPayment: boolean;
}

export interface AddBillingAddressPayload {
  name: string;
  address1: string;
  address2: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  phone: string;
}

export interface AddBillingAddressResponse {
  address: CheckoutBillingAddress;
}

export interface DeleteBillingAddressResponse {
  addressId: number;
}

export interface DownloadPromotion {
  discountType: string;
  discountAmount: string;
}

export interface ApplyDownloadPromoData {
  promotion: DownloadPromotion;
  beforeDiscountPrice: string;
  discountedPrice: string;
  freeCheckout?: DownloadPurchaseResult;
}

export const PROMO_DISCOUNT_TYPE_PERCENTAGE = 'PERCENTAGE';
export const AJAX_STATUS_SUCCESS = 'success';
export const AJAX_STATUS_FAIL = 'fail';
export const AJAX_STATUS_ERROR = 'error';

export const ONLINE_ADS_EVENT_TYPE = {
  DOWNLOAD_PURCHASE: 'download_purchase',
  TRIAL_START: 'trial_start',
  RESUBSCRIBED: 'resubscribed',
  SUBSCRIPTION_PURCHASE: 'subscription_purchase',
} as const;
export const PURCHASE_ORDER_TYPE_SUBSCRIPTION = 'subscription';
export const SUBSCRIPTION_PURCHASE_TRACKING_CACHE_KEY = 'pmw_subscription_purchase';
export const CACHE_EXPIRY_ONE_HOUR_MS = 3_600_000;
export const PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE = 'premiumUpsellDialogShown';
export const GA4_EVENT_NAME_TRIAL_START = 'TrialStart';
export const GA4_EVENT_NAME_NEW_SUBSCRIPTION_START = 'NewSubscriptionStart';
export const GA4_EVENT_NAME_SIGNUP_DIALOG_START_TRIAL = 'SignUpDialog_StartTrial';
export const PRICING_PAGE_PATH_SUFFIXES = ['/premium', '/premium/teams'];
export const SUBSCRIPTIONS_PAGE_URI = 'premium/subscriptions';
export const CREDIT_PACK_PURCHASE_SUCCESS_URI = 'premium/subscriptions?cpp=success';
export const CREDIT_PACK_PURCHASE_ERROR_URI = 'premium/subscriptions?cpp=error';
export const CARD_UPDATE_SUCCESS_PREMIUM_URI = 'premium/subscriptions?cu=success';
export const CARD_UPDATE_SUCCESS_FREE_URI = 'user/editprofile?cu=success';

export interface AjaxErrorResponse {
  code?: string;
  data?: {msg?: string};
  message?: string;
}

export interface CheckoutFlowController {
  goToStep: (step: CheckoutStep, direction?: TransitionDirection) => void;
  goBack: () => void;
  showPremiumConfirmation: (result: SubscriptionCheckoutResult) => void;
  fail: (title: string, message: string) => void;
  /** Closes the flow and runs the request's close side effects (close callback, post-confirmation reload). */
  exit: () => void;
  /** Closes the flow without any close side effects, for hand-offs to another dialog. */
  dismiss: () => void;
}

export enum PreflightOutcome {
  PROCEED = 'proceed',
  ABORT = 'abort',
  DISMISS = 'dismiss',
}

export interface CheckoutFlowInitializationHandlers {
  isCancelled: () => boolean;
  abort: () => void;
  dismiss: () => void;
}

export interface PendingCheckoutExit {
  request: CheckoutRequest;
  exit: () => void;
  timeoutId: number;
}

export interface UseCheckoutFlowOptions {
  onExit: () => void;
  /** Lets a host that embeds the flow return to its own content from the flow's first step. */
  onBackFromFirstStep?: () => void;
}

export interface CheckoutModalDimensions {
  width: string;
  height: string;
  mobileHeight: string;
  maxHeight?: string;
}

export interface CheckoutDragHandle {
  isVisible: boolean;
  isOnDarkPanel: boolean;
}

export const CHECKOUT_MODAL_HEIGHT = 'auto';
export const CHECKOUT_MODAL_MOBILE_HEIGHT = 'auto';

/**
 * The promo pill floats against the viewport at `bottom: 32px`, so a Stripe step taking the default
 * `100vh - 48px` reaches into the band the pill occupies and the pill lands on the form's Pay button.
 * The legacy dialog sized its box to the viewport less 176px, leaving the pill clear below it.
 * Only the Stripe steps take this: the credit-pack steps have their own fixed heights, which a
 * blanket cap would shrink on a short viewport.
 */
export const STRIPE_STEP_MODAL_MAX_HEIGHT = 'calc(100vh - 176px)';
/** Tall enough for the four packs plus the pinned footer; longer pack lists scroll inside the sheet. */
export const CREDIT_PACKS_COMPACT_MOBILE_HEIGHT = '520px';
/**
 * The tablet dialog is pinned so opening the credit-benefits view does not resize it — the pack
 * list settles at 527px, and the benefits list scrolls inside the same box rather than growing it.
 */
export const CREDIT_PACKS_TABLET_DIALOG_HEIGHT = '530px';

export interface CheckoutFlowRenderState {
  request: CheckoutRequest | null;
  isActive: boolean;
  step: CheckoutStep;
  isLoading: boolean;
  transitionDirection: TransitionDirection;
  contentKey: string;
  modalWidth: string;
  canGoBack: boolean;
  showsPanelHeader: boolean;
  showsPanelCloseIcon: boolean;
  controller: CheckoutFlowController;
}

export interface CheckoutFlowChrome {
  showsPanelHeader: boolean;
  showsPanelCloseIcon: boolean;
}

export interface CheckoutStepProps {
  request: CheckoutRequest;
  controller: CheckoutFlowController;
}

export interface CheckoutFlowPanelProps {
  request: CheckoutRequest;
  panelId: string;
  modalId: string;
  onExit: () => void;
}

export const US_COUNTRY_CODE = 'US';
export const US_STATE_UNSELECTED_VALUE = 'NA';
export const IAP_CREDITS_PRODUCT_TYPE = 'credits';
export const PAYPAL_CHECKOUT_SCRIPT_URL = 'https://www.paypalobjects.com/api/checkout.js';
export const PAYPAL_BUTTON_CONTAINER_ID = 'paypal-button';
export const PAYPAL_ERROR_LANG_KEY_BY_CODE: Record<string, string> = {
  credit_card_cvv_check_fail: 'pmwjs_card_security_code_incorrect',
  credit_card_refused: 'pmwjs_card_declined',
  currency_not_allowed: 'pmwjs_currency_not_allowed',
  insufficient_funds: 'pmwjs_insufficient_funds',
  payment_expired: 'pmwjs_payment_expired',
};

export interface PaypalAuthorizeData {
  paymentID: string;
  payerID: string;
}

export interface PaypalButtonRenderConfig {
  env: string;
  commit: boolean;
  style: Record<string, string | number | boolean>;
  payment: () => Promise<string | undefined>;
  onAuthorize: (data: PaypalAuthorizeData) => Promise<void>;
  onCancel: () => void;
  onError: (error: unknown) => void;
}

export interface PaypalSdk {
  Button: {
    render: (config: PaypalButtonRenderConfig, selector: string) => void;
  };
}

export interface AjaxResponseEnvelope<T> {
  status: string;
  data: T;
  code?: string;
}

export interface PaypalCheckoutStartData {
  account: string;
  cartId: number;
  id: string;
}

export interface IapTransaction {
  idTransaction: string;
}

export const CHECKOUT_HOST_PANEL_ID_SUFFIX = '-checkout';

export interface CheckoutHostContextValue {
  startCheckout: (request: CheckoutRequest) => void;
}

export interface CheckoutHostProviderProps {
  modalId: string;
  panelId: string;
  onCheckoutExit?: () => void;
  children: ReactNode;
}
